import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import type {
  BulkPermissionGateProps,
  PermissionState,
  RenderOptions,
} from '@/components/common/PermissionGate/types'
import {
  getDefaultTooltipMessage,
  renderWithPermission,
  validateAsChildProps,
} from '@/components/common/PermissionGate/utils'

import { useBulkPermissionCheck } from '@/hooks/permissions/usePermissionCheck'

import type { UUID } from '@/types/Id'
import type { Permission } from '@/types/Permission'

/**
 * Higher-Order Component for bulk resource permission checks.
 * Checks permissions on multiple resources simultaneously.
 *
 * @example
 * // Hide delete button if user can't delete ALL selected pursuits
 * <BulkPermissionGate
 *   resourceType="pursuit"
 *   resourceIds={selectedPursuitIds}
 *   permission="write"
 *   fallback="hide"
 * >
 *   <Button>Delete Selected</Button>
 * </BulkPermissionGate>
 *
 * @example
 * // Disable with tooltip for bulk operations
 * <BulkPermissionGate
 *   resourceType="file"
 *   resourceIds={selectedFileIds}
 *   permission="write"
 *   fallback="disable"
 *   tooltip="You need write permission on all selected files to sync them"
 *   asChild
 * >
 *   <DropdownMenuItem>Sync to Other Organizations</DropdownMenuItem>
 * </BulkPermissionGate>
 *
 * @example
 * // Require permission on ANY (not all) selected resources
 * <BulkPermissionGate
 *   resourceType="pursuit"
 *   resourceIds={selectedPursuitIds}
 *   permission="read"
 *   requireAll={false}
 *   fallback="disable"
 * >
 *   <Button>View Details</Button>
 * </BulkPermissionGate>
 */
export const BulkPermissionGate = forwardRef<
  HTMLElement,
  BulkPermissionGateProps
>(
  (
    {
      resourceType,
      resourceIds,
      permission,
      requireAll = true, // For bulk operations, usually need permission on ALL
      fallback = 'hide',
      fallbackComponent,
      loadingComponent,
      tooltip,
      tooltipProps,
      tooltipContentProps,
      children,
      asChild = false,
      onPermissionChange,
    },
    ref,
  ) => {
    // Validate props
    if (asChild) {
      validateAsChildProps(children, asChild)
    }

    // Normalize permission to array
    const permissions = useMemo(
      () => (Array.isArray(permission) ? permission : [permission]),
      [permission],
    )

    // Create select function to derive hasPermission directly from the hook
    const select = useCallback(
      (data: Map<UUID, Map<Permission, boolean>>) => {
        // When no resources selected, return true so business logic handles "nothing selected"
        // This avoids showing misleading permission denial tooltips
        if (resourceIds.length === 0) return true

        // Check each resource for required permissions
        const resourceResults = resourceIds.map((resourceId) => {
          const resourcePermissions = data.get(resourceId)
          if (!resourcePermissions) return false

          const permissionChecks = permissions.map(
            (p) => resourcePermissions.get(p) === true,
          )

          return requireAll
            ? permissionChecks.every(Boolean) // ALL permissions required on this resource
            : permissionChecks.some(Boolean) // ANY permission sufficient on this resource
        })

        return requireAll
          ? resourceResults.every(Boolean) // ALL resources must pass
          : resourceResults.some(Boolean) // ANY resource passing is sufficient
      },
      [resourceIds, permissions, requireAll],
    )

    const isEmptySelection = resourceIds.length === 0

    // Check bulk permissions with select to derive hasPermission directly
    const {
      data: hasPermissionFromHook,
      isLoading: isLoadingFromHook,
      isError: isErrorFromHook,
    } = useBulkPermissionCheck({
      resourceType,
      resourceIds,
      permissions,
      enabled: !isEmptySelection,
      select,
    })

    const hasPermission = isEmptySelection
      ? true
      : (hasPermissionFromHook ?? false)
    const isLoading = isEmptySelection ? false : isLoadingFromHook
    const isError = isEmptySelection ? false : isErrorFromHook

    // Track previous hasPermission value for change detection
    const prevHasPermission = useRef<boolean | null>(null)

    // Call onPermissionChange only when hasPermission actually changes
    useEffect(() => {
      if (
        onPermissionChange &&
        !isLoading &&
        prevHasPermission.current !== hasPermission
      ) {
        prevHasPermission.current = hasPermission
        onPermissionChange(hasPermission)
      }
    }, [hasPermission, isLoading, onPermissionChange])

    // Generate smart tooltip if not provided
    const finalTooltip =
      tooltip === undefined
        ? getDefaultTooltipMessage(resourceType, permission, 'bulk')
        : tooltip

    const permissionState: PermissionState = {
      hasPermission,
      isLoading,
      isError,
    }

    const renderOptions: RenderOptions = {
      fallback,
      fallbackComponent,
      loadingComponent,
      tooltip: finalTooltip,
      tooltipProps,
      tooltipContentProps,
    }

    const result = renderWithPermission(
      permissionState,
      children,
      renderOptions,
    )

    // Handle forwardRef when asChild is true
    if (asChild && React.isValidElement(result) && ref) {
      return React.cloneElement(result, {
        ref,
        ...(result.props || {}),
      } as any)
    }

    return result
  },
)

BulkPermissionGate.displayName = 'BulkPermissionGate'
